// The declared update order: which whole-room systems run after a world
// mutation, and in what order, expressed as data rather than as knowledge about
// where the calls happen to sit.
//
// Before this (2026-07-31, Phase 1), `systems.ts` held correct systems that were
// invoked by hand from every site that could invalidate them: the world-scoped
// `recomputeWaterFlow` from three places, `checkGateThreshold` from six. The
// dependency graph -- water flow reads object positions and object Energy; the
// gates read positions, conduction paths, and the reservoir charge the flow
// itself writes -- was real, but existed only as the order those calls happened
// to appear in. Adding a system, or a new mutation site, meant rediscovering it.
//
// Now a mutation site calls `settleWorld` and the graph decides. The order is
// declared in SIM_SYSTEMS below, each entry naming the systems it depends on,
// and a test asserts the array is a topological sort of its own declarations --
// so a system inserted in the wrong place fails a test instead of silently
// reading stale state.
import { WorldState } from './state';
import { SimEvents } from './events';
import { checkGateThreshold, checkRuneAltars, recomputeWaterFlow } from './systems';
import { settleVines } from './vines';
import { settleRust } from './rust';
import { settleRelics } from './relic';
import { settleBarrierRestores } from './mechanics';
import { settleSand } from './sand';
import { settleChargeBind } from './chargeBind';
import { settleCables } from './cable';
import { settleAutotiles } from './autotile';
import { settleChargeSources } from './chargeSource';
import { settleBreakers } from './breaker';
import { settleConveyors } from './conveyor';

export type SimSystemId =
  | 'rune-altars'
  | 'vines'
  | 'metal-rust'
  | 'relic-decay'
  | 'barrier-restore'
  | 'sand-phase'
  | 'water-flow'
  | 'charge-bind'
  | 'breaker-trip'
  | 'cable-shape'
  | 'autotile-shape'
  | 'conveyor-path'
  | 'charge-source'
  | 'conduit-gates';

export interface SimSystem {
  id: SimSystemId;
  /** Systems whose output this one reads, and which must therefore have already
   * run when it does. Declared per system rather than implied by array order;
   * `settleSystemsAreOrdered` (see sim.test.ts) checks the two agree. */
  dependsOn: SimSystemId[];
  /** What world state this system reads, in prose. Not machine-checked -- it is
   * here so that the *reason* for a dependency is written down next to it. */
  reads: string;
  /** Returning **true** means "I have invalidated something a system before me
   * already read", and `settleWorld` runs the whole order a second time.
   *
   * The declared order is a DAG and every back-edge would break it, so this is
   * the one way to express one -- and it is deliberately a *report* rather than
   * a dependency, because a back-edge that fires on every settle would be a
   * cycle rather than a graph. Exactly one system can return true today (see
   * `conduit-gates`), and only when a Sluice Gate actually moved. */
  run(world: WorldState, out: SimEvents): void | boolean;
}

/** The systems, in the order `settleWorld` runs them. */
export const SIM_SYSTEMS: readonly SimSystem[] = [
  {
    id: 'rune-altars',
    dependsOn: [],
    reads: 'whether every isRuneAltar object in the room reads altarFilled. A no-op until they all do, and self-cancelling afterward: the dormant object it swaps out is gone, so there is nothing left to activate.',
    run(world, out) {
      // First, because it is the one system that can add an object to the room
      // (room10's Portal replacing the Rune Circle) -- and the two below both
      // read the object set. Nothing today puts a Portal on a water tile or a
      // pressure pad, but the order costs nothing and means it would work if
      // something did.
      checkRuneAltars(world.rooms[world.player.roomId], out);
    },
  },
  {
    id: 'vines',
    dependsOn: ['rune-altars'],
    reads: 'every vines object\'s own properties.entropy/energy, and, for growth, the tile directly above it and whatever currently occupies it. Depended on nothing but rune-altars since 2026-08-16: entropy-bind used to sit above it as the one system that *wrote* properties.entropy, and Bind·Entropy no longer writes anything at settle time -- it displaces a cast\'s loci and is done (see sim/entropyBind.ts).',
    run(world, out) {
      // Near the front, for the same reason rune-altars runs first: this is
      // the only other system that can add an object to the room (a new
      // segment grown into the tile above), and water-flow/
      // conduit-gates below both read the object set. Nothing today places a
      // Gate Pillar or a pressure pad above a cliff, but the order costs
      // nothing and means it would work if something did.
      settleVines(world, out);
    },
  },
  {
    id: 'metal-rust',
    dependsOn: [],
    reads: "every rust object's own properties.entropy. Depended on entropy-bind until 2026-08-16, when Bind·Entropy stopped being a settle-time constraint -- nothing writes properties.entropy during a settle any more, so this reads whatever the cast that triggered the settle already moved. Never adds or removes an object, so its position is unconstrained; declared here because it is the purely-cosmetic per-object refresh next to vines'.",
    run(world, out) {
      settleRust(world, out);
    },
  },
  {
    id: 'relic-decay',
    dependsOn: [],
    reads: "every relic's own properties.entropy. Never adds or removes an object and reads nothing another system writes, so its position is unconstrained: declared beside metal-rust because it is the same kind of thing, a purely-presentational re-derivation of name/texture/description/signature from one live value. A relic at the far end of a Bind·Entropy link is still reached -- displacement means the *cast* landed on it, so the value was already moved by the time any of this runs.",
    run(world, out) {
      settleRelics(world, out);
    },
  },
  {
    id: 'barrier-restore',
    dependsOn: [],
    reads: "every collapsed object's own properties -- a candle's entropy against its own meltEntropy, and every other collapsed barrier's properties against its own meltsWhen -- restoring whichever have dropped back below. Reads nothing any other system writes and never adds or removes an object, so its position is unconstrained, same as metal-rust beside it. It used to depend on entropy-bind, for the case a cast-driven check would miss -- a Bind·Entropy partner drained without being cast at. Displacement removes that case rather than handling it: the far end *is* where the cast landed, so `applyTransferEffect`'s own `checkMeltThreshold` already covers it.",
    run(world, out) {
      const room = world.rooms[world.player.roomId];
      if (room) settleBarrierRestores(room, out);
    },
  },
  {
    id: 'sand-phase',
    dependsOn: [],
    reads: "every sand object's own properties.energy/entropy, and its own current sand.stage (the one thing here that isn't purely-live -- see sand.ts's header note on why Entropy's floor at 0 makes that memory necessary). Never adds or removes an object and reads nothing any *other* system writes, so its position is otherwise unconstrained, same as metal-rust/barrier-restore beside it.",
    run(world, out) {
      settleSand(world, out);
    },
  },
  {
    id: 'water-flow',
    dependsOn: ['rune-altars'],
    reads: 'object positions and walkability (a pushed obstacle dams the channel), the aqueduct\'s restored flag, and a riverbed tile\'s own water.frozen -- a persisted flag checkWaterFreeze (heatState.ts) sets, not read live from Energy.',
    run(world, out) {
      // Also feeds any powersReservoirId target the flow now reaches, which is
      // exactly why the gates have to run after this and not before: room9's
      // Substation gets its Charge from the wheel the water just reached.
      world.flowingTiles = recomputeWaterFlow(world.rooms[world.player.roomId], out);
    },
  },
  {
    id: 'charge-bind',
    dependsOn: ['water-flow'],
    reads: "every chargeBind link in the room, and the properties.charge of both loci at each end of one (plus the caster's own, for a link that names them). Depends on water-flow for two reasons, both dating from 2026-08-08: flowing water is conductive terrain, so which tiles flow decides what is on a given network at all (queries.chargeNetwork); and water-flow is the one system that *produces* Charge, filling a powersReservoir target the flow has reached. Before step 8 this declared no dependencies at all, on the grounds that no system produces Charge -- which the reservoir feed already made untrue, latently, for any room that wired one up.",
    run(world, out) {
      // A settle system rather than a room-decay one, unlike Bind·Energy: a
      // wire equalizes immediately and totally, so it has to resolve in the
      // same pass as the mutation that charged it, not up to a tick later.
      // See chargeBind.ts's module comment for why this is the correct model
      // and Bind·Energy's rate is still the correct one for heat.
      settleChargeBind(world, out);
    },
  },
  {
    id: 'breaker-trip',
    dependsOn: ['water-flow', 'charge-bind'],
    reads: "every breaker's own network potential (`queries.networkPotential`) against its own `breaker.trips` rating, and its own properties.entropy for which corrosion stage it repaints in. Depends on charge-bind, and through it on water-flow, because those are what *move* the Charge it is rated against -- a wire that dumps a Font into a line has to have dumped it before the breaker on that line decides whether the line is over its rating, not a settle later. Ordered before conduit-gates for the same reason from the other side: an open breaker is what those gates read as a broken path.",
    run(world, out) {
      // Trips are latched rather than live (see `sim/breaker.ts`): opening one
      // drops the potential that opened it, so a live reading would close it
      // again in the same pass and oscillate.
      settleBreakers(world, out);
    },
  },
  {
    id: 'cable-shape',
    dependsOn: ['breaker-trip'],
    reads: "every cable's own properties.entropy (which of its two palettes it wears) and whether each of its four orthogonal neighbours currently conducts -- a rusted fixture, or one with an open breaker in it, drops out of the drawn run at the same moment it drops out of the network, so the break in the line is drawn where the break in the circuit is. Depends on breaker-trip because a breaker that trips this pass has to be open before the run is drawn, or the picture is a settle behind the circuit. Never adds or removes an object and writes nothing any other system reads -- it is presentation only.",
    run(world, out) {
      settleCables(world, out);
    },
  },
  {
    id: 'autotile-shape',
    dependsOn: [],
    reads: "every autotiled object's own position and its four orthogonal neighbours' -- specifically whether each carries the same `autotile.key` (see sim/autotile.ts). Declares nothing, and genuinely has nothing to declare: the key is authored and no system writes one, positions are already final when a pass begins (a push, a cast and a mob step all settle *after* moving), and nothing else in the world reads a shape. Never adds or removes an object and writes only `texture` -- presentation only, exactly like cable-shape and conveyor-path beside it, and next to them for that reason.",
    run(world, out) {
      settleAutotiles(world, out);
    },
  },
  {
    id: 'conveyor-path',
    dependsOn: [],
    reads: "every belt's own position and its neighbours' -- a run is a connected component and a heading is that component read as a path (see sim/conveyor.ts) -- plus its own properties.entropy, for whether it is in the run at all and which corrosion palette it wears. Declares nothing: no system moves an object (a push, a cast and a mob step all settle *after* moving), and nothing writes properties.entropy during a settle, so the positions and the stages this reads are both already final when the pass begins. Never adds or removes an object and writes only conveyor.direction, which nothing else reads -- presentation, exactly like cable-shape above it, and next to it for that reason.",
    run(world, out) {
      // A belt's look is its corrosion stage crossed with its place in a run,
      // and a place in a run is a question about the room -- which is what took
      // it out of `settleRust` (where it lived while a heading was authored on
      // the tile) and put it here beside the cable, whose look is the same shape
      // of question. See `rust.ts`'s `ownsItsOwnLook`.
      settleConveyors(world, out);
    },
  },
  {
    id: 'charge-source',
    dependsOn: ['water-flow', 'charge-bind', 'breaker-trip'],
    reads: "every rated charge source's own network -- which consumers are on it, and whether each of them currently reads satisfied (isConduitCharged, the same call the gates make). Depends on the three systems that can reshape a network between one settle and the next: water-flow decides which tiles conduct and can feed a reservoir, charge-bind strings and equalizes a wire, and a tripped breaker cuts a run in half. Never adds or removes an object and writes nothing any other system reads -- presentation only, exactly like cable-shape beside it, and declared next to it for that reason. Runs before conduit-gates only incidentally; the two read the same feeds and neither writes what the other reads.",
    run(world, out) {
      settleChargeSources(world, out);
    },
  },
  {
    id: 'conduit-gates',
    dependsOn: ['rune-altars', 'water-flow', 'charge-bind', 'breaker-trip'],
    reads: "pressure-pad occupancy and conduit paths (both object positions), the caster's own tile (a pad.forPlayer pad is satisfied by them standing on it), plus the Charge held by each conduit's standing pillar -- which for room9 is a reservoir the water-flow system writes and for room15 is a locus the charge-bind system just equalized. Also whether each path tile's object currently reads rusted -- isConductive reads properties.entropy directly rather than anything metal-rust writes, and nothing writes Entropy during a settle at all since 2026-08-16, so there is no ordering to owe there.",
    run(world, out) {
      // Conduit *charge* itself is not a system: isConduitCharged recomputes it
      // live from the room, so there is no stored value to bring up to date.
      // What this pass does is act on it -- lighting the pillars and opening any
      // gate whose conditions now all read satisfied.
      //
      // `world.player` is handed over as the caster's tile, not as a whole
      // player: the only thing any gate condition asks about them is where they
      // are standing (see queries.CasterTile).
      //
      // Returns true when a **Sluice Gate** changed state, which is the one
      // thing this pass does that `water-flow` above already read -- see
      // `settleWorld` for why that is a second pass rather than a dependency.
      return checkGateThreshold(world.rooms[world.player.roomId], out, world.physics, world.player);
    },
  },
];

/** Runs every system over the player's current room, in declared dependency
 * order. Call this after mutating the world -- a push, a cast, a split, an
 * altar placement -- rather than picking out the systems the mutation "obviously"
 * affects; that judgement is what SIM_SYSTEMS now holds.
 *
 * Cheap enough to call unconditionally: every system is a pure recompute over one
 * room's objects, the altar check stops at its first unfilled altar, and
 * `recomputeWaterFlow` returns immediately in the ten rooms with no water source
 * at all.
 *
 * **One pass, or two, and never a fixpoint.** Until 2026-08-17 this was one pass
 * flat, on the grounds that a gate opening could not change the water flow --
 * true only because no gate shared a tile with a water object, which
 * `sim.test.ts` pinned so that the day one did, a test would say so rather than
 * the flow quietly lagging a mutation behind. The **Sluice Gate** is that gate:
 * `conduit-gates` decides whether it stands open and `water-flow`, four systems
 * earlier, decides what that lets through. So the back-edge is real, and this is
 * the second declared pass its own comment asked for rather than the while-loop
 * it warned against.
 *
 * The second pass runs the **whole** order, not just the systems below the
 * sluice, and that is the point: what an opened sluice changes is which tiles
 * carry fluid, and flowing water is conductive terrain, feeds a reservoir and
 * dams a channel. So the whole cascade -- flow, wire, breaker, gates -- resolves
 * inside the turn the lever was thrown, rather than water arriving on this move
 * and the circuit it completes noticing on the next.
 *
 * It is bounded at two, and the bound is honest rather than incidental: a sluice
 * whose *own* supply is admitted by a second sluice needs a third pass, and will
 * settle a move late. Nothing authored is that, no puzzle in the charter's shape
 * wants it, and the alternative -- looping to a fixpoint -- buys that case at the
 * price of a settle whose cost nobody can read off the declaration. */
export function settleWorld(world: WorldState, out: SimEvents): void {
  let again = false;
  for (const system of SIM_SYSTEMS) if (system.run(world, out)) again = true;
  if (!again) return;
  // The second pass is a plain re-run: every system here is a pure recompute
  // over one room, and the two that hold state across a pass are safe against
  // one -- a gate trigger's `satisfied` latch makes an unchanged condition a
  // no-op rather than a re-fire, and a tripped breaker stays tripped. Whatever
  // it reports is deliberately dropped; a third pass is where a bound would
  // stop being a bound.
  for (const system of SIM_SYSTEMS) system.run(world, out);
}
